import { useQuery } from '@tanstack/react-query'

import { auth } from '../../config/firebase'
import { toCamelCase } from '../../helpers/serializers'
import { toTherapist } from '../../helpers/transformers'
import type { Patient } from '../../types/Patient'
import type { ServiceLine } from '../../types/ServiceLine'
import type { Therapist } from '../../types/Therapist'

interface GetProvidersProps {
  patients: Partial<Patient>[]
  ignoreTakingNewPrivatePatients?: boolean
  sortBy?: string
  withFirstAvailability?: boolean
  enabled?: boolean
  onlyServiceLines?: ServiceLine[]
  clientId?: string
  licenseJurisdiction?: string
}

export interface ResultGetProviders {
  patient: Partial<Patient>
  availableServiceLinesNames: string[]
  therapists: Therapist[]
}

export const fetchProviders = async (params: GetProvidersProps) => {
  const token: string = await auth.currentUser.getIdToken()

  const response = await fetch(
    `${import.meta.env.VITE_DOTCOM_BFF_URL}providers`,
    {
      method: 'POST',
      body: JSON.stringify({
        eocIds: params.patients.flatMap((p) => p.carePlans?.map((cp) => cp.id)),
      }),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  ).then((response) => response.json())

  const result: ResultGetProviders[] = []

  const resultProviders: Therapist[] = response?.map((provider: any) =>
    toTherapist(toCamelCase(provider))
  )

  const therapists: Therapist[] = params.withFirstAvailability
    ? resultProviders.filter((p: Therapist) => Boolean(p.firstAvailability))
    : resultProviders

  const languages = [
    ...new Set(params.patients.map((p) => p.preferredLanguage)),
  ]

  // Sort therapists based on preferred language
  const sortedTherapists = therapists.sort((a, b) => {
    const aHasPreferredLanguage = languages.some((userLanguage: string) =>
      a.languages.some(
        (therapistLanguage: any) => therapistLanguage === userLanguage
      )
    )
    const bHasPreferredLanguage = languages.some((userLanguage: string) =>
      b.languages.some(
        (therapistLanguage: any) => therapistLanguage === userLanguage
      )
    )

    if (aHasPreferredLanguage && !bHasPreferredLanguage) return -1 // a comes first

    if (!aHasPreferredLanguage && bHasPreferredLanguage) return 1 // b comes first

    return 0 // no change in order
  })

  params.patients.forEach((patient) => {
    result.push({
      patient,
      availableServiceLinesNames: Array.from(
        new Set(sortedTherapists.map((t: Therapist) => t.serviceLine))
      ),
      therapists: sortedTherapists,
    })
  })

  return result
}

const useGetProviders = (params: GetProvidersProps) =>
  useQuery(['getProviders', params], async () => fetchProviders(params), {
    enabled:
      params.patients.flatMap((p) => p.carePlans?.map((cp) => cp.id)).length >
      0,
  })

export default useGetProviders
